/**
 * The Algolia fields the product tile reads (organisms/product/product-tile + helpers/algolia/map-product)
 * — the ONE `attributesToRetrieve` base for every product listing, on both paths:
 *
 *   - SERVER path: the V2 listing block (+ its configured facet paths, plp-dataset-core), the V2 slider
 *     (fetch-slider-dataset), and PDP related products (+ `key` and `attributes.IsNew`,
 *     fetch-related-products);
 *   - BROWSER path: /search (+ its Studio facet keys, client-retrieve-attributes).
 *
 * Every listing renders the same tile, so this list must not fork per surface: a surface adds only what
 * IT needs on top (facet paths to count, a field it filters or orders by). Editing this list changes the
 * Redis cache key of every server-path listing (the params ARE the key, backend AlgoliaApi), so each one
 * misses the cache once after deploy — harmless, but expect one round of cold Algolia fetches.
 *
 * It is also the biggest single lever on page weight: the retrieved hit IS the payload (Redis entry, SSR
 * HTML, edge cache). Measured on `methven-au` over the whole catalogue (179 products): `['*']` returned
 * 1,326 KB, this list 168 KB.
 *
 * Paths are the CANONICAL `attributes.*` spelling, never the `searchableAttributes.*` duplicate (see
 * attribute-paths.ts). The backend's slimForCache drops that duplicate anyway, and mapProduct only lifts
 * `hit.attributes` onto the product.
 *
 * Deliberately NOT here (verified against the index, not assumed):
 *   - `attributes.IsNew` — no tile reads it, and both products indices already rank new products first
 *     (`methven-au` / `methven-nz` are the new-first replicas). Only PDP related products adds it, because
 *     its backend pick is shuffled (see that tastic).
 *   - `variants.attributes.item-name` — requested by every old listing, read by none. The tile shows the
 *     product-level `name` on purpose: the catalogue stores the variant name in caps, so the tile shouted.
 *   - `attributes.item-name` / `attributes.item-code` — the tile prefers these (via `attributesAlgolia`)
 *     over `name` and the variant code, but neither exists at product level on ANY record in either index,
 *     so the tile always falls through to `name` + `variants[].attributes['item-code']`.
 *   - `objectID` — Algolia returns it on every hit regardless of `attributesToRetrieve`.
 *   - `images` — there is no product-level `images` field; the tiles read `variants[].images`.
 */
export const TILE_ATTRIBUTES = [
  // Product level
  'name', // tile title, image alt, and the name_asc / name_desc sort
  'slug', // PDP URL
  'categoryKeys', // category label on the tile (mapped to `categories[]`)
  'masterVariantID', // picks the default variant
  // Variant level
  'variants.id', // master-variant match + swatch keys
  'variants.key', // product-code fallback on the static (off-screen) tile
  'variants.sku', // PDP URL
  'variants.images', // tile image + carousel
  'variants.prices', // the "RRP" line
  'variants.attributes.colour', // swatches, PDP URL, chrome/stainless default variant
  'variants.attributes.item-code', // "Product code" line, ShopOnline, Product-IDs variant matching
  'variants.attributes.variant-name', // swatch dropdown label
  'variants.attributes.exclusive-product-NZ', // the "EXCLUSIVE TO …" badge on the NZ site
];
